import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { ProducerForm, ProducerFormData } from '@/components/admin/ProducerForm';
import { useAdminProducerContext } from '@/context/AdminProducerContext';
import { useToast } from '@/hooks/use-toast';
import { useDraftSessionId, clearDraftSessionId } from '@/hooks/useDraftSessionId';
import { finalizeDraftSession } from '@/lib/media/mediaApi';

const DRAFT_KEY = 'admin-producer-new';

export default function AdminProducerNew() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addProducer } = useAdminProducerContext();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const draftSessionId = useDraftSessionId(DRAFT_KEY);

  const handleSubmit = async (data: ProducerFormData) => {
    setIsSubmitting(true);
    try {
      const newProducer = await addProducer({
        name: data.name,
        shortDescription: data.shortDescription,
        longDescription: data.longDescription || undefined,
        address: data.address,
        city: data.city,
        region: data.region,
        lat: parseFloat(data.lat) || 0,
        lng: parseFloat(data.lng) || 0,
        categories: data.categories,
        phone: data.phone || undefined,
        website: data.website || undefined,
        instagram: data.instagram || undefined,
        email: data.email || undefined,
        openingHoursText: data.openingHoursText || undefined,
        coverUrl: data.coverUrl || undefined,
        galleryUrls: data.galleryUrls.filter(Boolean),
        status: data.status,
        verified: data.verified,
        producerStatus: 'pending',
        verificationSubmittedAt: null,
        verifiedAt: null,
        verificationNote: null,
      });

      // Finalize any draft media uploaded during creation
      try {
        console.log('[producer-save] finalize', { draftSessionId, producerId: newProducer.id });
        await finalizeDraftSession(draftSessionId, 'producer', newProducer.id);
        clearDraftSessionId(DRAFT_KEY);
      } catch (finalizeErr) {
        console.error('[producer-save] finalize failed (non-blocking)', finalizeErr);
      }

      // Invalidate media queries so images show immediately
      queryClient.invalidateQueries({ queryKey: ['media-assets', 'producer', newProducer.id] });

      toast({
        title: 'Värd skapad',
        description: `${data.name} har lagts till.`,
      });

      // Navigate to edit page for the new producer
      navigate(`/admin/producers/${newProducer.id}`);
    } catch (err) {
      console.error('Failed to create producer:', err);
      toast({
        title: 'Kunde inte skapa värd',
        description: 'Något gick fel. Kontrollera att du är inloggad som admin.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/producers');
  };

  return (
    <AdminLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-foreground">Ny värd</h1>
          <p className="text-muted-foreground mt-1">
            Lägg till en ny gård eller lokal värd.
          </p>
        </div>

        {/* Form */}
        <ProducerForm 
          onSubmit={handleSubmit} 
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      </div>
    </AdminLayout>
  );
}
